import React from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { TrendingDown } from '@mui/icons-material';
import { adminTokens as T } from '../../../theme/adminTokens';
import type { RegistrationFunnelStep } from '../../../api/adminApi';

export interface FunnelChartProps {
  title: string;
  subtitle?: string;
  steps: RegistrationFunnelStep[];
  loading?: boolean;
  emptyMessage?: string;
}

const fmtPct = (v: number) => `${(v * 100).toFixed(1)}%`;

const FunnelChart: React.FC<FunnelChartProps> = ({
  title, subtitle, steps, loading, emptyMessage = 'No funnel data available yet.',
}) => {
  return (
    <Box sx={{
      bgcolor: T.color.surface.raised,
      border: `1px solid ${T.color.surface.border}`,
      borderRadius: `${T.radius.lg}px`,
      p: `${T.space[5]}px`,
      boxShadow: T.shadow.sm,
      height: '100%',
    }}>
      <Box sx={{ mb: `${T.space[4]}px` }}>
        <Typography sx={{
          fontSize: T.type.size.lg, fontWeight: T.type.weight.semibold,
          color: T.color.text.primary, lineHeight: T.type.lineHeight.tight,
        }}>{title}</Typography>
        {subtitle && (
          <Typography sx={{ fontSize: T.type.size.sm, color: T.color.text.secondary, mt: `${T.space[1]}px` }}>
            {subtitle}
          </Typography>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${T.space[2]}px` }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} variant="rectangular" height={36} sx={{ borderRadius: `${T.radius.md}px` }} />
          ))}
        </Box>
      ) : steps.length === 0 ? (
        <Box sx={{
          py: `${T.space[6]}px`, textAlign: 'center',
          color: T.color.text.tertiary, fontSize: T.type.size.sm,
        }}>{emptyMessage}</Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${T.space[2]}px` }}>
          {steps.map((s, i) => {
            const widthPct = Math.max(8, s.conversionFromStart * 100);
            const isLast = i === steps.length - 1;
            return (
              <Box key={s.step} sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: `${T.space[3]}px`, mb: `${T.space[1]}px` }}>
                  <Box sx={{
                    flexShrink: 0,
                    width: 22, height: 22, borderRadius: '50%',
                    bgcolor: T.color.brand[50], color: T.color.brand[600],
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: T.type.size.xs, fontWeight: T.type.weight.bold,
                    fontVariantNumeric: 'tabular-nums',
                  }}>{i + 1}</Box>
                  <Typography sx={{
                    fontSize: T.type.size.sm, fontWeight: T.type.weight.semibold,
                    color: T.color.text.primary,
                    flex: 1, minWidth: 0,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{s.label}</Typography>
                  <Typography sx={{
                    fontSize: T.type.size.xs, color: T.color.text.secondary,
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                    {fmtPct(s.conversionFromStart)} · {s.reached.toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{
                  position: 'relative',
                  height: 10, ml: `${T.space[6] - T.space[2]}px`,
                  bgcolor: T.color.surface.sunken,
                  borderRadius: `${T.radius.pill}px`,
                  overflow: 'hidden',
                }}>
                  <Box sx={{
                    position: 'absolute', inset: 0,
                    width: `${widthPct}%`,
                    bgcolor: i === 0 ? T.color.brand[400] : T.color.brand[300],
                    borderRadius: `${T.radius.pill}px`,
                    transition: `width ${T.motion.medium}`,
                    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                  }} />
                </Box>
                {!isLast && i > 0 && s.dropoff > 0.05 && (
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: `${T.space[1]}px`,
                    ml: `${T.space[6] - T.space[2]}px`, mt: `${T.space[1]}px`,
                  }}>
                    <TrendingDown sx={{ fontSize: 12, color: T.color.error.text }} />
                    <Typography sx={{
                      fontSize: T.type.size.xs, color: T.color.error.text,
                      fontWeight: T.type.weight.semibold, fontVariantNumeric: 'tabular-nums',
                    }}>
                      {fmtPct(s.dropoff)} drop from previous step
                    </Typography>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default FunnelChart;
